import bemify from '../../utils/bemUtils';
import { FormFieldType } from '../../enums';
import Component from '../component/component';
import { getFormDataField, isFormValid, validateFormDataField } from '../../utils/formUtils';

/**
 * Represents a form that can dynamically generate fields based on JSON data.
 */
class Form extends Component {
  #formRef = null;
  #groupsRef = null;
  #fields = [];
  #formData = null;

  get captchaCallback() {
    return this.getAttribute('captcha-callback');
  }

  get captchaSiteKey() {
    return this.getAttribute('captcha-site-key');
  }

  /**
   * Gets the description of the form.
   */
  get description() {
    return this.getAttribute('description');
  }

  /**
   * Gets the form data used to generate the fields.
   */
  get formData() {
    return JSON.parse(decodeURIComponent(this.getAttribute('form-data') ?? '{}'));
  }

  /**
   * Sets the form data used to generate the fields.
   */
  set formData(value) {
    this.setAttribute('form-data', value);
  }

  /**
   * Gets the label of the submit button.
   */
  get submitLabel() {
    return this.getAttribute('submit-label') ?? 'Submit';
  }

  /**
   * Gets the title of the form.
   */
  get title() {
    return this.getAttribute('title');
  }

  get useCaptcha() {
    return this.hasAttribute('use-captcha');
  }

  initTemplate() {
    const [block, element] = bemify('form');
    const template = this.createTemplate('template');
    template.innerHTML += `
      <form
        class="${block()}"
        autocomplete="off"
        novalidate
        part="form">
        <slot></slot>
        ${
          this.title
            ? `
            <fieldset>
              <legend>
                <h2
                  class="${element('title')}"
                  part="title">${this.title}</h2>
              </legend>
            </fieldset>
          `
            : ''
        }
        ${
          this.description
            ? `
            <p
              class="${element('description')}"
              part="description">${this.description}</p>
          `
            : ''
        }
        <div part="groups">${this.#generateFormGroupItems()}</div>
        <div
          class="${element('footer')}"
          part="footer">
          <sk-button>
            ${this.submitLabel}
          </sk-button>
        </div>
      </form>
    `;

    this.initShadowRoot(template.content.cloneNode(true));
  }

  onMounted() {
    this.#initForm();
  }

  /**
   * Called once to return the attributes to observe.
   */
  static get observedAttributes() {
    return ['form-data'];
  }

  onAttributeChange() {
    this.#initForm();
  }

  /**
   * Resets the form.
   */
  reset() {
    for (const field of this.#fields) {
      if (field.type !== 'hidden') {
        field.value = '';
        field.checked = false;
      }

      const requiredMessage = field?.offsetParent?.querySelector('.form__required-message');
      requiredMessage?.classList.remove('form__required-message--visible');

      const invalidMessage = field?.offsetParent?.querySelector('.form__invalid-message');
      invalidMessage?.classList.remove('form__invalid-message--visible');
    }
  }

  #generateFormGroupItems() {
    const [_, element] = bemify('form');

    this.#formData = this.formData;

    if (Object.isEmpty(this.#formData)) {
      return '';
    }

    return this.#formData?.groups
      ?.map(
        group => `
      <div class="${element('group')}">
        ${
          group?.isInline
            ? `
            ${
              group?.label
                ? `
              <label
                class="${element('label')}"
                for="${group?.fields[0].id}">
                ${group?.label}
              </label>
            `
                : ''
            }
            <div class="${element('group', 'inline')}">${this.#generateFormFieldItems(group)}</div>
          `
            : this.#generateFormFieldItems(group)
        }
      </div>
    `
      )
      .join('');
  }

  #generateFormFieldItems(group) {
    const [_, element] = bemify('form');
    const getFormFieldItem = field => {
      let fieldMarkup = '';

      if (field?.label) {
        fieldMarkup = `
          <label
            class="${element('label')}"
            for="${field?.id}">
            ${field?.label}
            ${field?.isRequired ? `<span class="${element('required')}">*</span>` : ''}
          </label>
        `;
      }

      if (field?.type === FormFieldType.checkbox) {
        fieldMarkup += `
          <sk-checkbox
            id="${field?.id}"
            class="${element('field')}"
            ${field?.isChecked ? this.addAttributeMarkup('default-checked', '', false) : ''}
            label="${field?.subLabel}"
            name="${field?.name}">
          </sk-checkbox>
        `;
      } else if (field?.type === FormFieldType.radio) {
        fieldMarkup += `
          <sk-radio
            id="${field?.id}"
            class="${element('field')}"
            ${field?.isChecked ? this.addAttributeMarkup('checked', '', false) : ''}
            label="${field?.subLabel}"
            name="${field?.name}">
          </sk-radio>
        `;
      } else if (field?.type === FormFieldType.select) {
        fieldMarkup += `
          <sk-dropdown
            id="${field?.id}"
            class="${element('field')}"
            items="${encodeURIComponent(JSON.stringify(field?.options))}"
            name="${field?.name}">
          </sk-dropdown>
        `;
      } else if (field?.type === FormFieldType.textarea) {
        fieldMarkup += `
          <sk-text-field
            id="${field?.id}"
            class="${element('field')}"
            multiline
            name="${field?.name}">
          </sk-text-field>
        `;
      } else {
        fieldMarkup += `
          <sk-text-field
            id="${field?.id}"
            class="${element('field')}"
            name="${field?.name}"
            type="${field?.type}"
            ${this.addAttributeMarkup('value', field?.value)}>
          </sk-text-field>
          ${
            field?.subLabel
              ? `
            <label
              class="${element('label', 'sub-label')}"
              for="${field?.id}">
              ${field?.subLabel}
              ${field?.isRequired ? `<span class="${element('required')}">*</span>` : ''}
            </label>
          `
              : ''
          }
        `;
      }

      if (field?.requiredMessage) {
        fieldMarkup += `<span class="${element('required-message')}">${field?.requiredMessage}</span>`;
      }

      if (field?.invalidMessage) {
        fieldMarkup += `<span class="${element('invalid-message')}">${field?.invalidMessage}</span>`;
      }

      return fieldMarkup;
    };

    return group?.fields
      .map(
        field => `
      ${
        group?.isInline
          ? `<div class="${element('inline-group')}">${getFormFieldItem(field)}</div>`
          : getFormFieldItem(field)
      }
    `
      )
      .join('');
  }

  #initFieldEvents() {
    const fields = this.shadowRoot?.querySelectorAll('.form__field');

    for (const field of fields) {
      let underlyingField = field?.shadowRoot?.querySelector('input');

      if (!underlyingField && field?.type === FormFieldType.text) {
        underlyingField = field?.shadowRoot?.querySelector('textarea');
      } else if (!underlyingField && field?.type === FormFieldType.select) {
        underlyingField = field?.shadowRoot?.querySelector('button');
      }

      this.#fields.push(underlyingField);

      field?.addEventListener('on-change', ({ detail }) => this.#onValidateField(detail));
      field?.addEventListener('on-blur', ({ detail }) => this.#onValidateField(detail));
    }

    const slot = this.shadowRoot?.querySelector('slot');
    const hiddenFields = slot?.assignedElements();

    this.#fields = this.#fields.concat(Array.from(hiddenFields));
  }

  #initForm() {
    this.#initFormEvents();
    this.#initFormData();
    this.#initFieldEvents();
  }

  #initFormData() {
    if (this.#groupsRef && !Object.isEmpty(this.formData)) {
      this.#groupsRef.innerHTML = this.#generateFormGroupItems();
    }
  }

  #initFormEvents() {
    if (!this.#formRef) {
      this.#formRef = this.shadowRoot?.querySelector('[part="form"]');
      this.#formRef.addEventListener('submit', e => this.#onSubmit(e));

      this.#groupsRef = this.shadowRoot?.querySelector('[part="groups"]');

      const submit = this.shadowRoot?.querySelector('[part="footer"] sk-button');
      submit?.addEventListener('on-click', () => this.#formRef.requestSubmit());
    }
  }

  #onSubmit(e) {
    e.preventDefault();

    if (isFormValid(this.#formData, this.#fields)) {
      this.#submitFormData();
    }
  }

  #onValidateField(e) {
    const field = e.target;
    const formDataField = getFormDataField(this.#formData, field.id);

    validateFormDataField(formDataField, this.#fields);
  }

  #submitFormData() {
    const formData = new FormData();

    for (const field of this.#fields) {
      formData.set(field?.name, field?.value);
    }

    this.fireCustomEvent('on-submit', formData);
  }
}

customElements.define('sk-form', Form);

export default Form;
